/**
 * Gemini API client. Hybrid key: user localStorage first, then env fallback.
 * Key priority: localStorage ld_gemini → VITE_GEMINI_API_KEY → GEMINI_API_KEY.
 * Never blocks user if local key missing; falls back to env.
 */
import { getStoredGeminiKeys, mergeApiKeys } from "./apiKeys";

const GEMINI_MODEL = "gemini-2.5-flash";
const GEMINI_API_BASE = "https://generativelanguage.googleapis.com/v1beta/models";
const GEMINI_TIMEOUT_MS = 60_000;
const GEMINI_RETRY_DELAY_MS = 2_000;
const GEMINI_MAX_RETRIES = 2;

const USE_PROXY = import.meta.env.PROD || !!import.meta.env.VITE_AI_PROXY_ENABLED;
const PROXY_BASE = (import.meta.env.VITE_AI_PROXY_URL as string) || "";

function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 503;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve the first effective Gemini key (local override first, env fallback last).
 * Note: process.env.GEMINI_API_KEY is server-side only; used by the proxy.
 */
export function getGeminiKey(): string {
  return getStoredGeminiKeys()[0] ?? "";
}

function parseGeminiResponse(data: unknown): string | null {
  const candidates = (data as { candidates?: Array<{ content?: { parts?: Array<{ text?: string; thought?: boolean }> } }> })?.candidates;
  const text = candidates?.[0]?.content?.parts?.filter((part) => !part.thought).map((part) => part.text ?? "").join("");
  return typeof text === "string" && text.trim() ? text : null;
}

function isRateLimitOrQuotaError(res: Response, data: unknown): boolean {
  if (res.status === 429) return true;
  const msg = String((data as { error?: { message?: string } })?.error?.message ?? "").toLowerCase();
  return msg.includes("quota") || msg.includes("rate limit") || msg.includes("resource exhausted");
}

function shouldTryNextGeminiKey(status: number | null | undefined, data: unknown, errorMessage: string): boolean {
  const payloadMsg = String((data as { error?: { message?: string } })?.error?.message ?? "");
  const msg = `${payloadMsg} ${errorMessage}`.toLowerCase();
  if (status === 429) return true;
  if (status === 401 || status === 403) return true;
  if (status === 400 && /api key|invalid|permission|forbidden|denied|unauthorized|quota|resource exhausted/.test(msg)) {
    return true;
  }
  return /quota|rate limit|resource exhausted|api key|permission|forbidden|denied|unauthorized|expired/.test(msg);
}

export interface CallGeminiOptions {
  systemInstruction?: string;
  maxOutputTokens?: number;
  temperature?: number;
  parts?: GeminiContentPart[];
}

export type GeminiContentPart =
  | { text: string }
  | { inlineData: { mimeType: string; data: string } };

async function callGeminiDirect(
  prompt: string,
  geminiApiKey: string,
  emptyErrorMsg: string,
  options: CallGeminiOptions
): Promise<string> {
  const { systemInstruction, maxOutputTokens = 1500, temperature = 0.7, parts } = options;
  const url = `${GEMINI_API_BASE}/${GEMINI_MODEL}:generateContent?key=${encodeURIComponent(geminiApiKey)}`;

  const body: Record<string, unknown> = {
    contents: [{ parts: parts && parts.length > 0 ? parts : [{ text: prompt }] }],
    generationConfig: { maxOutputTokens, temperature, thinkingConfig: { thinkingBudget: 0 } },
  };
  if (systemInstruction?.trim()) {
    body.systemInstruction = { parts: [{ text: systemInstruction.trim() }] };
  }

  let lastErr: Error | null = null;
  for (let attempt = 0; attempt <= GEMINI_MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GEMINI_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const rawText = await res.text();
      let data: unknown;
      try {
        data = JSON.parse(rawText);
      } catch {
        console.error("[gemini] API returned non-JSON. Status:", res.status, "Raw (first 500 chars):", rawText?.slice(0, 500));
        throw new Error(emptyErrorMsg);
      }

      if (!res.ok) {
        const errMsg = (data as { error?: { message?: string } })?.error?.message || `HTTP ${res.status}`;
        if (isRetryableStatus(res.status) && attempt < GEMINI_MAX_RETRIES) {
          console.warn("[gemini] Retryable error, attempt", attempt + 1, "/", GEMINI_MAX_RETRIES + 1, ":", res.status, errMsg);
          await sleep(GEMINI_RETRY_DELAY_MS);
          lastErr = new Error(errMsg);
          continue;
        }
        console.error("[gemini] API error. Status:", res.status, "Message:", errMsg);
        throw Object.assign(new Error(errMsg), { res, data });
      }

      const text = parseGeminiResponse(data);
      if (text) return text;
      const blockReason = (data as { candidates?: Array<{ finishReason?: string }> })?.candidates?.[0]?.finishReason;
      console.error("[gemini] Empty text. finishReason:", blockReason);
      throw new Error(blockReason === "SAFETY" ? "Content was blocked by safety filters. Try a different topic." : emptyErrorMsg);
    } catch (e) {
      clearTimeout(timeoutId);
      if (e instanceof Error && e.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      const err = e instanceof Error ? e : new Error(String(e));
      const status = (e as { res?: Response })?.res?.status;
      if (status != null && isRetryableStatus(status) && attempt < GEMINI_MAX_RETRIES) {
        console.warn("[gemini] Retryable error, attempt", attempt + 1, "/", GEMINI_MAX_RETRIES + 1, ":", status);
        await sleep(GEMINI_RETRY_DELAY_MS);
        lastErr = err;
        continue;
      }
      throw err;
    }
  }
  throw lastErr ?? new Error(emptyErrorMsg);
}

async function callGeminiViaProxy(
  prompt: string,
  geminiApiKeys: string[],
  emptyErrorMsg: string,
  options: CallGeminiOptions
): Promise<string> {
  const url = `${PROXY_BASE}/api/ai/gemini`;
  const userKeys = mergeApiKeys(geminiApiKeys);
  const firstUserKey = userKeys[0];

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (firstUserKey) headers["X-Gemini-Key"] = firstUserKey;

  const body = JSON.stringify({
    prompt,
    geminiApiKeys: userKeys,
    parts: options.parts,
    systemInstruction: options.systemInstruction,
    maxOutputTokens: options.maxOutputTokens ?? 1500,
    temperature: options.temperature ?? 0.7,
  });

  let lastErr: Error | null = null;
  for (let attempt = 0; attempt <= GEMINI_MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GEMINI_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers,
        body,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const raw = await res.text();
      let data: unknown;
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        console.error("[gemini] Proxy returned non-JSON. Status:", res.status, "Raw (first 500):", raw?.slice(0, 500));
        throw new Error(res.ok ? emptyErrorMsg : `HTTP ${res.status}`);
      }

      if (!res.ok) {
        const errMsg = (data as { error?: string })?.error || `HTTP ${res.status}`;
        if (isRetryableStatus(res.status) && attempt < GEMINI_MAX_RETRIES) {
          console.warn("[gemini] Proxy retryable error, attempt", attempt + 1, "/", GEMINI_MAX_RETRIES + 1, ":", res.status);
          await sleep(GEMINI_RETRY_DELAY_MS);
          lastErr = new Error(errMsg);
          continue;
        }
        console.error("[gemini] Proxy error. Status:", res.status, "Message:", errMsg);
        throw new Error(errMsg);
      }

      const text = (data as { text?: string })?.text;
      if (typeof text === "string" && text.trim()) return text.trim();
      const backendMsg = (data as { error?: string })?.error;
      throw new Error(backendMsg || emptyErrorMsg);
    } catch (e) {
      clearTimeout(timeoutId);
      if (e instanceof Error && e.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      const err = e instanceof Error ? e : new Error(String(e));
      const statusMatch = err.message.match(/HTTP (\d+)/);
      const status = statusMatch ? parseInt(statusMatch[1], 10) : null;
      if (status != null && isRetryableStatus(status) && attempt < GEMINI_MAX_RETRIES) {
        console.warn("[gemini] Proxy retryable, attempt", attempt + 1, "/", GEMINI_MAX_RETRIES + 1, ":", status);
        await sleep(GEMINI_RETRY_DELAY_MS);
        lastErr = err;
        continue;
      }
      throw err;
    }
  }
  throw lastErr ?? new Error(emptyErrorMsg);
}

/**
 * Call the Gemini API. Key priority: 1) User's key from props/store, 2) localStorage ld_gemini, 3) VITE_GEMINI_API_KEY.
 * Proxy mode: server uses process.env.GEMINI_API_KEY when no X-Gemini-Key is sent.
 */
export async function callGemini(
  prompt: string,
  geminiApiKeys: string | string[],
  emptyErrorMsg: string,
  options: CallGeminiOptions = {}
): Promise<string> {
  const keysFromProps = Array.isArray(geminiApiKeys) ? geminiApiKeys : geminiApiKeys ? [geminiApiKeys] : [];
  const keys = mergeApiKeys(keysFromProps, getStoredGeminiKeys());
  const keyFromProps = keysFromProps.find((k) => k?.trim())?.trim();
  const keyFromStorage = getStoredGeminiKeys()[0] ?? "";

  if (keys.length > 0) {
    const source = keyFromProps ? "1.props/store (user Settings)" : keyFromStorage ? "2.localStorage (ld_gemini) or 3.VITE_GEMINI_API_KEY" : "none";
    console.log("[gemini] API key source:", source, "| keysFromProps:", keysFromProps.length, "| totalCandidates:", keys.length);
  } else {
    console.log("[gemini] No client key found. Proxy will use shared server keys or env fallback.");
  }

  if (USE_PROXY) {
    return callGeminiViaProxy(prompt, keys, emptyErrorMsg, options);
  }

  if (keys.length === 0) {
    throw new Error("No Gemini API key configured. Add a key in Settings or set VITE_GEMINI_API_KEY (dev) / GEMINI_API_KEY (deployment).");
  }

  let lastError: Error | null = null;
  for (let index = 0; index < keys.length; index++) {
    const key = keys[index];
    try {
      return await callGeminiDirect(prompt, key, emptyErrorMsg, options);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      const status = (e as { res?: Response })?.res?.status;
      const data = (e as { data?: unknown })?.data;
      const hasMoreKeys = index < keys.length - 1;
      if (hasMoreKeys && shouldTryNextGeminiKey(status, data, err.message)) {
        console.warn("[gemini] Key failed, trying next configured key.", {
          failedKeyIndex: index + 1,
          remainingKeys: keys.length - index - 1,
          status: status ?? "unknown",
        });
        lastError = err;
        continue;
      }
      throw err;
    }
  }

  throw lastError ?? new Error(emptyErrorMsg);
}
